<template>
  <div class="cav">
    <div class="cav-head">
      <div class="cav-head__main">
        <div class="cav-head__title">{{ $t('clientActivity.title') }}</div>
        <div class="cav-head__sub">{{ $t('clientActivity.subtitle') }}</div>
      </div>

      <div class="cav-head__picker">
        <!-- selected client chip -->
        <div v-if="selectedClient" class="cav-selected">
          <span class="cav-avatar">{{ initials(selectedClient) }}</span>
          <span class="cav-selected__name text-capitalize">{{ selectedClient.clientFullname || (selectedClient.firstname + ' ' + selectedClient.lastname) }}</span>
          <span class="cav-selected__meta tabular">#{{ selectedClient.id }}</span>
          <button type="button" class="cav-selected__clear" @click="clearClient"><i class="fa-solid fa-xmark"/></button>
        </div>

        <!-- free-text search: any part of first or last name, case-insensitive -->
        <div v-else class="cav-search">
          <i class="fa-solid fa-magnifying-glass cav-search__icon"/>
          <b-form-input v-model="search" class="cav-search__input" autocomplete="off"
                        :placeholder="$t('clientActivity.searchPlaceholder')"
                        @input="onSearchInput" @focus="showResults = true" @blur="onBlur"/>
          <b-spinner v-if="searching" small class="cav-search__spinner"/>

          <div v-if="showResults && search && (results.length || !searching)" class="cav-search__results">
            <button v-for="client in results" :key="client.id" type="button" class="cav-search__row"
                    @mousedown.prevent="selectClient(client)">
              <span class="cav-avatar">{{ initials(client) }}</span>
              <span class="cav-search__name text-capitalize">{{ client.clientFullname || (client.firstname + ' ' + client.lastname) }}</span>
              <span class="cav-search__meta">{{ client.email }}</span>
              <span class="cav-search__id tabular">#{{ client.id }}</span>
            </button>
            <div v-if="!results.length && !searching" class="cav-search__none">{{ $t('clientActivity.noResults') }}</div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="clientId" class="cav-panel">
      <client-activity-timeline :client-id="clientId" @open-contract="openContract"/>
    </div>
    <div v-else class="cav-empty">
      <i class="fa-solid fa-user-clock"/>
      <p>{{ $t('clientActivity.selectClient') }}</p>
    </div>

    <contract-rent-sidebar ref="contractRentSidebar"/>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import {Debounce} from '@core/utils/utils'
import ClientActivityTimeline from '@/components/panels/client/ClientActivityTimeline.vue'
import ContractRentSidebar from '@/components/sidebar/ContractRent/ContractRentSidebar.vue'

export default {
  name: 'ClientActivityView',
  components: {ClientActivityTimeline, ContractRentSidebar},
  data: () => ({
    search: '',
    results: [],
    searching: false,
    showResults: false,
    selectedClient: null,
    searchDebounce: Debounce(() => {}, 350),
  }),
  computed: {
    clientId() {
      return this.selectedClient ? Number(this.selectedClient.id) : null
    },
  },
  created() {
    this.searchDebounce = Debounce(this.runSearch, 350)
  },
  methods: {
    ...mapActions('clients', ['getClients']),
    onSearchInput() {
      if (!this.search) {
        this.results = []
        this.searching = false
        return
      }
      this.searching = true
      this.showResults = true
      this.searchDebounce()
    },
    runSearch() {
      const text = this.search
      if (!text) {
        this.results = []
        this.searching = false
        return
      }
      // the backend free-text search matches first/last name, email and phone,
      // case-insensitive — same filter the client picker modal uses
      this.getClients({page: 1, amountPerPage: 8, filter: {search: text, active: 1}})
          .then(data => {
            if (text === this.search) {
              this.results = data.clients || []
            }
          })
          .finally(() => { this.searching = false })
    },
    selectClient(client) {
      this.selectedClient = client
      this.showResults = false
      this.search = ''
      this.results = []
    },
    clearClient() {
      this.selectedClient = null
      this.search = ''
      this.results = []
    },
    onBlur() {
      setTimeout(() => { this.showResults = false }, 200)
    },
    initials(client) {
      const first = (client.firstname || ' ')[0] || ''
      const last = (client.lastname || ' ')[0] || ''
      return (first + last).toUpperCase().trim() || '?'
    },
    openContract(contractId) {
      if (contractId) this.$refs.contractRentSidebar.showContract(contractId)
    },
  },
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

.cav-head { display: flex; align-items: flex-start; gap: 16px; flex-wrap: wrap; padding-bottom: 12px; }
.cav-head__title { font-size: 1.15rem; font-weight: 800; color: #171e30; letter-spacing: -.01em; }
.cav-head__sub { font-size: .74rem; color: #9aa2b1; margin-top: 2px; }
.cav-head__picker { margin-left: auto; flex: 0 1 420px; min-width: 280px; position: relative; }

.cav-avatar {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .62rem; font-weight: 800; color: #fff; background: #3b6ff6;
}

.cav-search {
  position: relative;
  &__icon {
    position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
    color: #9aa2b1; pointer-events: none; z-index: 1; font-size: .72rem;
  }
  &__input { padding-left: 32px; height: 36px; border-color: rgba(23, 30, 48, .12); border-radius: 8px; }
  &__spinner { position: absolute; right: 11px; top: 50%; transform: translateY(-50%); color: #3b6ff6; }
  &__results {
    position: absolute; top: calc(100% + 5px); left: 0; right: 0; z-index: 30;
    background: #fff; border-radius: 9px; overflow: hidden;
    border: 1px solid rgba(23, 30, 48, .12);
    box-shadow: 0 14px 30px -12px rgba(23, 30, 48, .35);
  }
  &__row {
    display: flex; align-items: center; gap: 9px; width: 100%;
    padding: 8px 11px; border: none; background: transparent;
    text-align: left; cursor: pointer;
    &:hover { background: rgba(59, 111, 246, .07); }
    + .cav-search__row { border-top: 1px solid rgba(23, 30, 48, .05); }
  }
  &__name { font-weight: 700; color: #171e30; font-size: .8rem; white-space: nowrap; }
  &__meta { font-size: .68rem; color: #9aa2b1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__id { margin-left: auto; font-size: .66rem; font-weight: 700; color: #3b6ff6; white-space: nowrap; }
  &__none { padding: 10px 12px; font-size: .74rem; color: #9aa2b1; }
}

.cav-selected {
  display: flex; align-items: center; gap: 9px;
  padding: 5px 8px; border-radius: 8px;
  background: rgba(59, 111, 246, .06);
  border: 1px solid rgba(59, 111, 246, .3);
  &__name { font-weight: 700; color: #171e30; font-size: .84rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &__meta { font-size: .68rem; font-weight: 700; color: #3b6ff6; }
  &__clear {
    margin-left: auto; border: none; background: transparent; cursor: pointer;
    color: #9aa2b1; padding: 3px 6px; border-radius: 6px; font-size: .74rem;
    &:hover { color: #e05252; background: rgba(224, 82, 82, .08); }
    &:focus { outline: none; }
  }
}

.cav-panel {
  background: #fff; border: 1px solid rgba(23, 30, 48, .1); border-radius: 10px;
  padding: 16px 18px;
}
.cav-empty {
  text-align: center; color: #9aa2b1; padding: 70px 0;
  i { font-size: 2.2rem; opacity: .3; }
  p { margin-top: 10px; font-size: .84rem; }
}

@media (max-width: 767px) {
  .cav-head__picker { margin-left: 0; width: 100%; flex-basis: 100%; }
}
</style>

<style lang="scss">
.dark-layout {
  .cav-head__title { color: #eef1f7; }
  .cav-panel { background: #232b42; border-color: rgba(255, 255, 255, .1); }
  .cav-search__results { background: #232b42; border-color: rgba(255, 255, 255, .12); }
  .cav-search__row:hover { background: rgba(91, 139, 255, .1); }
  .cav-search__name, .cav-selected__name { color: #eef1f7; }
  .cav-search__input { border-color: rgba(255, 255, 255, .14); }
  .cav-selected { background: rgba(91, 139, 255, .1); border-color: rgba(91, 139, 255, .35); }
}
</style>
